import BaseEntity from './BaseEntity';
import { EEntityStatus } from '../types/entity';
import WindowModel from './2d/windowModel';
import WindowModel3d from './3d/windowModel';
import { EDoorWindowType, IWindowData } from '../data/model/model';
import { Point2dUtils } from '../math/Point2dUtils';
import { cloneDeep, isBoolean, isNumber, isString } from 'lodash-es';
import { EEntityType } from '../types';
import { syncCenterTypeToDistanceType } from '../utils/window';

export interface IWindowEntityUpdateParams {
  extendedWindowData: IWindowData;
  status: EEntityStatus;
  selected: boolean;
  isEntrance: boolean;
  forceUpdate: boolean;
}

// WindowEntity更新对比的是center、rotation、thickness，而不会对比distance、side、relativeWallInfo
class WindowEntity extends BaseEntity<EEntityType.DoorWindow> {
  protected override _model2d: WindowModel;
  protected override _model3d: WindowModel3d;

  private _extendedWindowData: IWindowData;

  private _status: EEntityStatus = EEntityStatus.normal;
  private _isEntrance: boolean = false;

  override set selected(value: boolean) {
    this.update({ selected: value });
  }

  override get selected() {
    return this._selected;
  }

  getData(): IWindowData {
    // 进行一次数据同步
    this._extendedWindowData = syncCenterTypeToDistanceType(this._extendedWindowData);
    return this._extendedWindowData;
  }

  get visible() {
    return this._visible;
  }

  setVisible(visible: boolean) {
    this._model2d.setVisible(visible);
    this._model3d.setVisible(visible);
    this._visible = visible;
  }

  setIsEntrance(isEntrance: boolean) {
    if (this._isEntrance !== isEntrance) {
      this.update({ isEntrance });
    }
  }

  constructor(extendedWindowData: IWindowData) {
    const { id = '', modelId: brandgoodId = '', type = EDoorWindowType.window } = extendedWindowData;

    super({ type: EEntityType.DoorWindow, id, brandgoodId: brandgoodId });

    this._model2d = new WindowModel({ id: id, type: type });
    this._model3d = new WindowModel3d(id);

    this._extendedWindowData = cloneDeep(extendedWindowData);
    this.update({ forceUpdate: true });
  }

  override update(options: Partial<IWindowEntityUpdateParams> = {}) {
    const { status, selected, forceUpdate, isEntrance } = options;
    const { modelId, width, thickness, height, offGround, flip, rotation, center } = options.extendedWindowData || {};

    let destroyUpdate = forceUpdate || !this._model2d.rendered || false;
    let noDestroyUpdate = false;

    // =================== 变更判断、重新赋值 ===================
    if (isString(modelId) && this._extendedWindowData.modelId !== modelId) {
      destroyUpdate = true;
    }
    if (isNumber(width) && width !== this._extendedWindowData.width) {
      destroyUpdate = true;
    }
    if (isNumber(height) && height !== this._extendedWindowData.height) {
      destroyUpdate = true;
    }
    if (isNumber(thickness) && thickness !== this._extendedWindowData.thickness) {
      destroyUpdate = true;
    }
    if (isBoolean(flip) && flip !== this._extendedWindowData.flip) {
      destroyUpdate = true;
    }
    if (isNumber(offGround) && offGround !== this._extendedWindowData.offGround) {
      noDestroyUpdate = true;
    }

    if (isNumber(rotation) && rotation !== this._extendedWindowData.rotation) {
      noDestroyUpdate = true;
    }

    if (center && !Point2dUtils.equals(center, this._extendedWindowData.center)) {
      noDestroyUpdate = true;
    }

    if (isBoolean(selected) && selected !== this._selected) {
      this._selected = selected;
      noDestroyUpdate = true;
    }

    if (isBoolean(isEntrance) && isEntrance !== this._isEntrance) {
      this._isEntrance = isEntrance;
      this._selected = false;
      noDestroyUpdate = true;
    }

    if (status && status !== this._status) {
      this._status = status;
      noDestroyUpdate = true;
    }

    if (options.extendedWindowData) {
      // 进行一次数据覆盖
      Object.assign(this._extendedWindowData, options.extendedWindowData);
    }

    // =================== 没有更新 ===================
    if (!destroyUpdate && !noDestroyUpdate) return;

    // =================== 准备数据 ===================
    const params = {
      center: this._extendedWindowData.center,
      rotation: this._extendedWindowData.rotation,
      width: this._extendedWindowData.width,
      thickness: this._extendedWindowData.thickness,
      height: this._extendedWindowData.height,
      offGround: this._extendedWindowData.offGround,
      flip: this._extendedWindowData.flip,
      modelId: this._extendedWindowData.modelId,
      status: this._status,
      selected: this._selected,
      isEntrance: this._isEntrance,
      type: this._extendedWindowData.type
    };
    if (destroyUpdate) {
      this._model2d.update(params);
      this._model3d.update(params);
    } else if (noDestroyUpdate) {
      this._model2d.updateWithoutDestroy(params);
      this._model3d.updateWithoutDestroy(params);
    }
  }

  clear() {
    this._model2d.clear();
    this._model3d.clearModel();
  }
}

export default WindowEntity;
